import React, { useState, useEffect, memo } from 'react';
import { fetchHive } from '../helpers/fetchMetadata';

interface ISelectedHiveProps {
  hiveId: number;
  address: string | undefined | null;
  addressHiveTokenIds: number[];
  addressHoney: number;
  combCount: { [id: number]: number };
  setCombCount: (counts: { [id: number]: number }) => void;
  txPending: boolean;
  updateHoneyCost: (rarityIndex: number, add: boolean) => void;
  rarityIndex: number;
  claimedCombs?: number;
  maxCombs?: number;
}

interface IHiveMetadata {
  name: string;
  image: string;
  description: string;
  attributes: any[];
}

const SelectedHive: React.FC<ISelectedHiveProps> = (props) => {
  const [hiveData, setHiveData] = useState<IHiveMetadata>();
  const maxCombsPerHive = props.maxCombs ?? 2;
  const claimedCombs = Math.min(maxCombsPerHive, props.claimedCombs ?? 0);
  const availableCombs = Math.max(0, maxCombsPerHive - claimedCombs);

  useEffect(() => {
    if (props.hiveId > 0) {
      // Fetch actual hive metadata
      fetchHive(props.hiveId)
        .then((response) => {
          setHiveData(response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch hive metadata:', error);
          // Fallback to placeholder data
          setHiveData({
            name: `Hive #${props.hiveId}`,
            image: `https://example.com/hive/${props.hiveId}.png`,
            description: `Hive ${props.hiveId}`,
            attributes: []
          });
        });
    }
  }, [props.hiveId]);

  const changeCombCount = (change: number) => {
    const currentCount = props.combCount[props.hiveId] || 0;
    const newCount = currentCount + change;

    if (newCount >= 0 && newCount <= availableCombs) {
      const newCombCount = { ...props.combCount };
      newCombCount[props.hiveId] = newCount;
      props.updateHoneyCost(props.rarityIndex, change === 1);
      props.setCombCount(newCombCount);
    }
  };

  const currentSelection = props.combCount[props.hiveId] || 0;
  const honeycombStatusMessage = availableCombs === 0
    ? 'All honeycombs extracted'
    : `${availableCombs} honeycomb${availableCombs === 1 ? '' : 's'} available`;

  return (
    <div className="bg-black bg-opacity-80 rounded-md shadow-lg p-3 flex flex-col items-center w-full border border-[#55bac3]">
      {/* Header */}
      <div className="text-[#c4f6f9] text-sm mb-2">
        Hive #{props.hiveId}
      </div>
      
      {/* Image */}
      {hiveData?.image && (
        <img 
          src={hiveData.image} 
          alt={hiveData.name}
          className="w-24 h-24 rounded-md mb-3 border border-[#c4f6f9]"
          onError={(e) => {
            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9Ijk2IiBoZWlnaHQ9Ijk2IiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjQ4IiB5PSI1NiIgZmlsbD0iI0ZGRiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiPkhpdmU8L3RleHQ+CjwvdGV4dD4KPC9zdmc+';
          }}
        />
      )}

      {/* Selection Controls */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => changeCombCount(-1)}
          disabled={currentSelection <= 0}
          className="w-8 h-8 bg-transparent border-2 border-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed text-[#ffa2f8] rounded-full flex items-center justify-center font-bold transition-colors"
        >
          -
        </button>
        
        <span className="text-white text-sm min-w-[2ch] text-center">
          {currentSelection}
        </span>
        
        <button
          onClick={() => changeCombCount(1)}
          disabled={currentSelection >= availableCombs}
          className="w-8 h-8 bg-transparent border-2 border-[#ffa2f8] hover:bg-[#ffa2f8] hover:text-black disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed text-[#ffa2f8] rounded-full flex items-center justify-center font-bold transition-colors"
        >
          +
        </button>
      </div>

      {/* Status Text */}
      <div className={`text-sm text-center ${availableCombs === 0 ? 'text-gray-500' : 'text-[#55bac3]'}`}>
        {honeycombStatusMessage}
      </div>
    </div>
  );
};

export default memo(SelectedHive, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.hiveId === nextProps.hiveId &&
    prevProps.address === nextProps.address &&
    prevProps.addressHiveTokenIds === nextProps.addressHiveTokenIds &&
    prevProps.addressHoney === nextProps.addressHoney &&
    prevProps.combCount === nextProps.combCount &&
    prevProps.txPending === nextProps.txPending &&
    prevProps.updateHoneyCost === nextProps.updateHoneyCost &&
    prevProps.rarityIndex === nextProps.rarityIndex &&
    prevProps.claimedCombs === nextProps.claimedCombs &&
    prevProps.maxCombs === nextProps.maxCombs
  );
});